<template lang="html">
  <div>
    <div
      :class="[$slots.announcement && 'brand-bar-placeholder--announcement']"
      class="brand-bar-placeholder"
    />
    <div
      ref="header"
      :class="[
        scrolledBelowHeader && 'brand-bar--sticky',
        !pageHeaderVisible() && 'brand-bar--invisible',
        pageHeaderVisible() && 'brand-bar--visible',
        $slots.announcement && 'brand-bar--announcement',
      ]"
      class="brand-bar"
    >
      <slot name="announcement" />
      <div class="brand-bar__inner">
        <div class="brand-bar__bar">
          <div class="brand-bar__container">
            <AppLink :is-link="!!homeUrl" :to="homeUrl">
              <LogoStiftung
                v-if="logoImage === 'stiftung'"
                class="brand-bar__logo"
              />
              <ImageEmpty
                v-if="logoImage === 'stiftung'"
                :image-title="'Beisheim Stiftung'"
              />
              <LogoHolding
                v-if="logoImage === 'holding'"
                :site="$route.params.country === 'ch' ? 'holding' : 'capital'"
                class="brand-bar__logo"
              />
              <ImageEmpty
                v-if="logoImage === 'holding'"
                :image-title="
                  $route.params.country === 'ch'
                    ? 'Beisheim Holding'
                    : 'Beisheim Capital'
                "
              />
            </AppLink>
            <h1
              v-if="logoImage === 'stiftung'"
              class="brand-bar__hl-hidden"
              v-html="'Beisheim Stiftung'"
            />
            <h1
              v-if="logoImage === 'holding'"
              class="brand-bar__hl-hidden"
              v-html="
                $route.params.country === 'ch'
                  ? 'Beisheim Holding'
                  : 'Beisheim Capital'
              "
            />
            <div class="brand-bar__actions">
              <slot />
            </div>
            <div v-if="$slots['additional']" class="brand-bar__additional">
              <slot name="actions" />
              <slot name="additional" />
              <slot name="toggle" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueTypes from "vue-types";
import throttle from "lodash/throttle";
import LogoStiftung from "beisheim/components/elements/logo-stiftung";
import LogoHolding from "beisheim/components/elements/logo-holding";
import ImageEmpty from "beisheim/components/elements/image-empty";
import AppLink from "beisheim/components/elements/app-link";

export default {
  components: {
    LogoStiftung,
    LogoHolding,
    ImageEmpty,
    AppLink,
  },
  props: {
    homeUrl: VueTypes.string.isRequired,
    logoImage: VueTypes.string.isRequired,
  },
  data() {
    return {
      lastScrollY: 0,
      currentDimensions: {
        height: 0,
      },
      initialDimensions: {
        height: 0,
      },
      scrolling: false,
      scrollingDown: false,
      scrolledBelowHeader: false,
      throttleScrollHandler: throttle(this.update, 200),
      throttleResizeHandler: throttle(this.init, 200),
    };
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.throttleScrollHandler);
    window.removeEventListener("resize", this.throttleResizeHandler);
    this.throttleScrollHandler = null;
    this.throttleResizeHandler = null;
  },
  mounted() {
    this.init();
    window.setTimeout(this.init, 100);
    window.addEventListener("load", this.init);
    window.addEventListener("scroll", this.throttleScrollHandler);
    window.addEventListener("resize", this.throttleResizeHandler);
  },
  methods: {
    getCurrentPosition() {
      return window.scrollY;
    },
    getDimensions() {
      if (this.$refs.header) {
        return this.$refs.header.getBoundingClientRect();
      }
    },
    init() {
      this.initialDimensions = this.getDimensions();
      this.currentDimensions = this.getDimensions();
      this.lastScrollY = this.getCurrentPosition();
    },
    update() {
      this.currentDimensions = this.getDimensions();

      if (this.lastScrollY < this.getCurrentPosition()) {
        this.scrollingDown = true;
      } else {
        this.scrollingDown = false;
      }

      if (this.getCurrentPosition() > this.initialDimensions.height) {
        this.scrolledBelowHeader = true;
      } else {
        this.scrolledBelowHeader = false;
      }

      this.lastScrollY = this.getCurrentPosition();
    },
    pageHeaderVisible() {
      if (this.scrolledBelowHeader && this.scrollingDown) {
        this.$store.commit("ui/hidePageHeader");
        return false;
      }
      this.$store.commit("ui/showPageHeader");
      return true;
    },
  },
};
</script>

<style lang="scss">
@import "beisheim/scss/scale";
@import "beisheim/scss/colors";
@import "beisheim/scss/grid";
@import "beisheim/scss/breakpoints";

$animation-time: 0.2s;

$header-height: 8.5rem;
$header-height-small: 7rem;
$announcement-height: 2.3rem;

.brand-bar-placeholder {
  height: $header-height;
  @include to(5) {
    height: $header-height-small;
  }
}

.brand-bar {
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  height: $header-height;
  @include to(5) {
    height: $header-height-small;
  }
}

.brand-bar-placeholder--announcement {
  height: $header-height + $announcement-height;
  @include to(5) {
    height: $header-height-small + $announcement-height;
  }
}

.brand-bar--announcement {
  height: $header-height + $announcement-height;
  @include to(5) {
    height: $header-height-small + $announcement-height;
  }
}

.brand-bar__inner {
  display: flex;
  background-color: gray(5);
}

.brand-bar--sticky {
  will-change: transform;
  transition: transform $animation-time ease-in-out;
  height: auto;

  .brand-bar__bar {
    padding-top: scale(2);
    padding-bottom: scale(2);
  }
}

.brand-bar--visible {
  transform: translateY(0%);
}

.brand-bar--invisible {
  transform: translateY(-100%);
}

.brand-bar__bar {
  width: 100%;
  transition: padding-top $animation-time ease-in-out,
    padding-bottom $animation-time ease-in-out;
  will-change: padding-top, padding-bottom;
  padding-top: scale(6);
  padding-bottom: scale(6);
  @include grid-spacing(padding-left, 1);
  @include grid-spacing(padding-right, 1);
}

.brand-bar__container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.brand-bar__body {
  display: flex;
  align-items: center;
}

.brand-bar__logo {
  display: block;
  min-width: 9rem;
  margin-right: scale(4);
  max-width: 200px;
  width: 100%;

  @include from(5) {
    max-width: 360px;
  }
}

.brand-bar__actions {
  display: flex;
  justify-content: flex-end;
}

.brand-bar__additional {
  display: flex;
}

.brand-bar__hl-hidden {
  display: none;
}
</style>
